class KanelaMobileNavigation extends HTMLElement {
  connectedCallback() {
    if (this.listeners) return;
    this.listeners = new AbortController();
    this.cache ||= new Map();
    this.scrollArea = this.querySelector('[data-nav-scroll]');
    this.rootPanel = this.querySelector('[data-nav-root]');
    this.panel = this.querySelector('[data-nav-panel]');
    this.heading = this.querySelector('[data-nav-heading]');
    this.backButton = this.querySelector('[data-nav-back]');
    this.status = this.querySelector('[data-nav-status]');
    this.drawer = this.closest('menu-drawer');
    this.reset();
    this.addEventListener('click', this.onClick.bind(this), { signal: this.listeners.signal });
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape' && (this.stack.length > 1 || this.request)) {
        event.preventDefault();
        event.stopPropagation();
        this.goBack();
      }
    }, { signal: this.listeners.signal });
    this.observer = new MutationObserver(() => {
      if (!this.drawer.hasAttribute('open')) this.reset();
    });
    this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['open'] });
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.listeners = null;
    this.observer?.disconnect();
    this.cancelRequest();
  }

  cancelRequest() {
    this.request?.abort();
    this.request = null;
    this.removeAttribute('data-loading');
    this.scrollArea?.removeAttribute('aria-busy');
  }

  reset() {
    this.cancelRequest();
    this.stack = [{ title: this.dataset.menuLabel, scroll: 0, markup: null, url: null }];
    this.status.hidden = true;
    this.status.replaceChildren();
    this.render(false);
  }

  onClick(event) {
    if (event.target.closest('[data-nav-back]')) { this.goBack(); return; }
    const link = event.target.closest('a[data-nav-open]');
    if (!link || !this.contains(link) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || this.stack.some(entry => entry.url === url.pathname)) return;
    if (!link.dataset.navTemplate && !url.pathname.includes('/collections/')) return;
    event.preventDefault();
    this.enter({ title: link.dataset.navTitle, url: url.pathname, template: link.dataset.navTemplate });
  }

  async enter(target) {
    this.cancelRequest();
    this.status.replaceChildren();
    this.status.hidden = true;
    const request = new AbortController();
    this.request = request;
    try {
      let markup;
      const template = target.template && [...this.querySelectorAll('template[data-nav-template-content]')].find(node => node.dataset.navTemplateContent === target.template);
      if (template) markup = template.content.firstElementChild.outerHTML;
      else if (this.cache.has(target.url)) markup = this.cache.get(target.url);
      else {
        this.setAttribute('data-loading', '');
        this.scrollArea.setAttribute('aria-busy', 'true');
        this.status.textContent = this.dataset.loadingLabel;
        this.status.hidden = false;
        const url = new URL(target.url, window.location.origin);
        url.searchParams.set('section_id', 'kanela-navigation-branch');
        const timeout = setTimeout(() => request.abort(), 15000);
        let response;
        try { response = await fetch(url, { signal: request.signal, credentials: 'same-origin' }); }
        finally { clearTimeout(timeout); }
        if (!response.ok) throw new Error('Navigation unavailable');
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const branch = parsed.querySelector('[data-nav-branch]');
        if (!branch || new URL(branch.dataset.navUrl, window.location.origin).pathname !== target.url) throw new Error('Invalid navigation branch');
        markup = branch.outerHTML;
        this.cache.set(target.url, markup);
      }
      if (this.request !== request || request.signal.aborted) return;
      this.stack.at(-1).scroll = this.scrollArea.scrollTop;
      this.stack.push({ ...target, markup, scroll: 0 });
      this.status.hidden = true;
      this.render(true);
    } catch (error) {
      if (this.request !== request) return;
      this.status.hidden = false;
      const message = document.createElement('span');
      message.textContent = this.dataset.errorLabel + ' ';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = this.dataset.retryLabel;
      retry.addEventListener('click', () => this.enter(target), { once: true });
      const fallback = document.createElement('a');
      fallback.href = target.url;
      fallback.textContent = this.dataset.viewLabel;
      this.status.replaceChildren(message, retry, fallback);
    } finally {
      if (this.request === request) {
        this.request = null;
        this.removeAttribute('data-loading');
        this.scrollArea.removeAttribute('aria-busy');
      }
    }
  }

  goBack(index = this.stack.length - 2) {
    this.cancelRequest();
    this.status.hidden = true;
    if (index < 0 || index >= this.stack.length - 1) return;
    const returnUrl = this.stack[index + 1].url;
    this.stack.length = index + 1;
    this.render(false);
    const panel = index === 0 ? this.rootPanel : this.panel;
    const link = [...panel.querySelectorAll('a[data-nav-open]')].find(node => new URL(node.href, window.location.origin).pathname === returnUrl);
    (link || this.heading).focus({ preventScroll: true });
  }

  render(focusHeading) {
    const current = this.stack.at(-1);
    this.rootPanel.hidden = this.stack.length > 1;
    this.panel.hidden = this.stack.length === 1;
    this.panel.innerHTML = current.markup || '';
    this.heading.textContent = current.title;
    this.heading.classList.toggle('visually-hidden', this.stack.length === 1);
    this.backButton.hidden = this.stack.length === 1;
    this.scrollArea.scrollTop = current.scroll;
    if (focusHeading) this.heading.focus({ preventScroll: true });
  }
}
if (!customElements.get('kanela-mobile-navigation')) customElements.define('kanela-mobile-navigation', KanelaMobileNavigation);
